import { useEffect, useRef } from 'react';
import { BACKEND } from '@/lib/api';

/**
 * Subscribe to a WebSocket channel and invoke `onUpdate(msg)` whenever a
 * non-connection message arrives. Handles React StrictMode double-invoke,
 * auto-reconnects after 5s on disconnect, and cleans up on unmount.
 *
 * @param {string} path - the ws path (e.g. `/api/ws/launchpad/global` or `/api/ws/marketplace`)
 * @param {(msg: any) => void} onUpdate
 * @param {Set<string> | string[]} [acceptTypes] - if provided, only these message.type values invoke onUpdate
 */
function useWSPath(path, onUpdate, acceptTypes) {
  const cbRef = useRef(onUpdate);
  useEffect(() => { cbRef.current = onUpdate; }, [onUpdate]);

  useEffect(() => {
    if (!path) return undefined;
    const base = (BACKEND || '').replace(/^http/, 'ws');
    const url = `${base}${path}`;
    let ws = null;
    let retryTimer = null;
    let disposed = false;
    const accept = acceptTypes ? new Set(acceptTypes) : null;

    const connect = () => {
      if (disposed) return;
      try { ws = new WebSocket(url); }
      catch { retryTimer = setTimeout(connect, 5000); return; }
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (!msg || !msg.type) return;
          if (msg.type === 'connected' || msg.type === 'ping') return;
          if (accept && !accept.has(msg.type)) return;
          cbRef.current && cbRef.current(msg);
        } catch { /* ignore */ }
      };
      ws.onclose = () => {
        if (disposed) return;
        retryTimer = setTimeout(connect, 5000);
      };
      ws.onerror = () => { /* onclose will handle retry */ };
    };
    connect();

    return () => {
      disposed = true;
      if (retryTimer) clearTimeout(retryTimer);
      if (ws) {
        try {
          if (ws.readyState === WebSocket.OPEN) ws.close(1000, 'unmount');
          else ws.onclose = null;
        } catch { /* noop */ }
      }
    };
    // acceptTypes intentionally not in deps — treated as static per-caller
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);
}

export default function useLaunchpadWS(channel, onUpdate) {
  return useWSPath(channel ? `/api/ws/launchpad/${channel}` : null, onUpdate, ['leaderboard_update']);
}

export function useMarketplaceWS(onUpdate) {
  return useWSPath('/api/ws/marketplace', onUpdate, ['listing_created', 'listing_sold', 'listing_cancelled']);
}

